/* Notification Center → Pop-up Settings: the lab's NAMED in-app alert pop-up configurations
 * (2026-10-14; before that there was one lab-wide setting). Each setting says where the pop-up
 * sits (9 positions) and whether it closes by itself after N seconds or stays until Acknowledge.
 * Exactly one is the Default; a notification rule may be assigned another one (Rule → Delivery
 * Method). A rule marked ACK REQUIRED always needs Acknowledge, whatever its setting says.
 *
 * List → New / Edit / View in a modal → Save through the shared useSaveFlow; Delete through the
 * shared useDeleteFlow. The server is authoritative for every rule below (unique name, one
 * Default, never delete one that rules use); the checks here only save the user a round trip. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormTitle } from '../../../components/FormTitle';
import { AppToolbar } from '../../../components/AppToolbar';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { useConfirm, useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import {
  ALERT_POPUP_POSITIONS, ALERT_POPUP_MIN_SECONDS, ALERT_POPUP_MAX_SECONDS,
  positionLabel, setAlertPopupSettings, type AlertPopupPosition, type AlertPopupMode,
} from './alertPopupSettings';
import {
  createPopupSetting, deletePopupSetting, listPopupSettings, popupRefusal, updatePopupSetting,
  type PopupSetting, type PopupSettingInput,
} from './inboxApi';
import { useInboxStore } from './inboxStore';
import './inbox.css';
import './popupSettings.css';

export const PAGE_ID = 'nc-popup-settings';
/** The audit FormName (CLAUDE.md §14b.2 registry) — matches PopupSettingsController.AuditFormName. */
export const AUDIT_FORM = 'Notification Pop-up Settings';
const NAME_MAX = 100;

type DialogMode = 'new' | 'edit' | 'view';
interface Draft {
  name: string; position: AlertPopupPosition; mode: AlertPopupMode; secondsText: string;
  isDefault: boolean; isActive: boolean;
}

function draftOf(s?: PopupSetting, firstEver = false): Draft {
  return s
    ? { name: s.name, position: s.position, mode: s.mode, secondsText: String(s.seconds), isDefault: s.isDefault, isActive: s.isActive }
    : { name: '', position: 'bottom-right', mode: 'auto', secondsText: '8', isDefault: firstEver, isActive: true };
}

const closeLabel = (s: Pick<PopupSetting, 'mode'>) => (s.mode === 'ack' ? 'Acknowledge' : 'Auto Close');
const durationLabel = (s: Pick<PopupSetting, 'mode' | 'seconds'>) => (s.mode === 'auto' ? `${s.seconds} s` : '—');

export default function PopupSettingsPage() {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const [rows, setRows] = useState<PopupSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | undefined>();
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [dlg, setDlg] = useState<{ mode: DialogMode; setting?: PopupSetting } | null>(null);
  const [draft, setDraft] = useState<Draft>(draftOf());
  const [replaceFor, setReplaceFor] = useState<PopupSetting | null>(null);
  const [replacementId, setReplacementId] = useState('');

  const load = useCallback(async (quiet = false) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const list = await listPopupSettings();
      setRows(list);
      // The Default is the client's fallback for alerts that carry no setting of their own.
      const def = list.find((r) => r.isDefault && r.isActive);
      if (def) setAlertPopupSettings({ position: def.position, mode: def.mode, seconds: def.seconds });
      setSelected((sel) => new Set([...sel].filter((id) => list.some((r) => r.id === id))));
      if (!quiet) setLoadMs(Math.round(performance.now() - t0));
    } catch {
      notify.error('Could not load the pop-up settings. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const selectedRow = useMemo(
    () => (selected.size === 1 ? rows.find((r) => r.id === [...selected][0]) : undefined), [selected, rows]);

  // ── dialog ────────────────────────────────────────────────────────────────
  function openDialog(mode: DialogMode, setting?: PopupSetting) {
    setDraft(draftOf(setting, rows.length === 0));
    setDlg({ mode, setting });
  }
  const closeDialog = () => setDlg(null);

  const seconds = Number(draft.secondsText);
  const secondsValid = Number.isInteger(seconds) && seconds >= ALERT_POPUP_MIN_SECONDS && seconds <= ALERT_POPUP_MAX_SECONDS;
  const nameTrim = draft.name.trim();
  const isNew = dlg?.mode === 'new';
  const readOnly = dlg?.mode === 'view';
  /** The current Default cannot be unticked — pick ANOTHER setting as Default instead. */
  const lockedDefault = !!dlg?.setting?.isDefault || (isNew && rows.length === 0);

  function toInput(): PopupSettingInput {
    return {
      name: nameTrim, position: draft.position, mode: draft.mode,
      seconds: secondsValid ? seconds : (dlg?.setting?.seconds ?? 8),
      isDefault: draft.isDefault, isActive: draft.isActive || draft.isDefault,
    };
  }

  function handleSave() {
    if (!dlg || readOnly) return;
    if (!nameTrim) { notify.error('Pop-up Name is required.'); return; }
    if (draft.mode === 'auto' && !secondsValid) {
      notify.error(`Auto-close seconds must be a whole number from ${ALERT_POPUP_MIN_SECONDS} to ${ALERT_POPUP_MAX_SECONDS}.`);
      return;
    }
    const input = toInput();
    const existing = dlg.setting;
    void runSave({
      formName: AUDIT_FORM,
      auditAction: isNew ? 'create' : 'edit',
      save: async () => { if (existing) await updatePopupSetting(existing.id, input); else await createPopupSetting(input); },
      onSaved: () => { closeDialog(); void load(true); },
      onError: (e) => notify.error(popupRefusal(e)?.message ?? 'The pop-up setting could not be saved.'),
    });
  }

  /** Push ONE clearly-marked sample pop-up built from the given position / mode / seconds. It lives only
   *  in the client store (`preview: true`) — nothing is sent, logged or written, and several can be shown
   *  independently. */
  function showPreview(name: string, popup: { position: AlertPopupPosition; mode: AlertPopupMode; seconds: number }) {
    useInboxStore.setState((s) => ({
      popups: [...s.popups, {
        id: `preview-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, preview: true, ruleCode: 'PREVIEW',
        ruleName: name || 'Pop-up preview',
        severity: 'info', ackRequired: false, subject: '[PREVIEW] Sample notification — nothing was sent',
        summary: `This is how a pop-up using “${name || 'this setting'}” looks. No notification, email or SMS was sent.`,
        createdAt: new Date().toISOString(), details: [],
        popup,
      }],
    }));
  }

  /** Show a sample pop-up with the settings currently in the dialog, so the admin sees what users get. */
  function showTest() {
    showPreview(nameTrim, { position: draft.position, mode: draft.mode, seconds: secondsValid ? seconds : 8 });
  }

  // ── delete ────────────────────────────────────────────────────────────────
  async function requestDelete(s: PopupSetting) {
    if (s.ruleCount > 0) {
      await alert({
        title: 'Cannot delete', tone: 'warning',
        message: `“${s.name}” is assigned to ${s.ruleCount} notification rule${s.ruleCount === 1 ? '' : 's'}. ` +
          'Open each rule, choose another pop-up setting under Delivery Method, then delete this one.',
      });
      return;
    }
    if (s.isDefault) {
      const others = rows.filter((r) => r.id !== s.id && r.isActive);
      if (others.length === 0) {
        await alert({
          title: 'Cannot delete', tone: 'warning',
          message: `“${s.name}” is the Default and there is no other Active setting to take its place. Create or activate another setting first.`,
        });
        return;
      }
      setReplacementId(others[0]!.id);
      setReplaceFor(s);
      return;
    }
    await doDelete(s);
  }

  async function doDelete(s: PopupSetting, replacement?: string) {
    await runDelete({
      formName: AUDIT_FORM,
      message: `Delete the pop-up setting “${s.name}”?`,
      delete: async () => { await deletePopupSetting(s.id, replacement); },
      onDeleted: () => { setSelected(new Set()); void load(true); },
      onError: (e) => notify.error(popupRefusal(e)?.message ?? 'The pop-up setting could not be deleted.'),
    });
  }

  async function confirmReplacement() {
    if (!replaceFor || !replacementId) return;
    const target = replaceFor;
    const next = rows.find((r) => r.id === replacementId);
    const ok = await confirm({
      title: 'Change the Default?', tone: 'danger', confirmLabel: 'Change Default',
      message: `“${next?.name}” becomes the Default and “${target.name}” is deleted.`,
    });
    if (!ok) return;
    setReplaceFor(null);
    await doDelete(target, replacementId);
  }

  // ── grid ──────────────────────────────────────────────────────────────────
  const columns: AppColumn<PopupSetting>[] = [
    { field: 'name', header: 'Pop-up Name', minWidth: 180 },
    { field: 'position', header: 'Position', minWidth: 130, text: (r) => positionLabel(r.position), render: (r) => positionLabel(r.position) },
    { field: 'mode', header: 'Close Behavior', minWidth: 130, text: closeLabel, render: closeLabel },
    { field: 'seconds', header: 'Duration', align: 'right', width: 100, text: durationLabel, render: durationLabel },
    { field: 'isDefault', header: 'Default', align: 'center', width: 90, text: (r) => (r.isDefault ? 'Yes' : 'No'), render: (r) => (r.isDefault ? 'Yes' : 'No') },
    { field: 'isActive', header: 'Active', align: 'center', width: 90, text: (r) => (r.isActive ? 'Yes' : 'No'), render: (r) => (r.isActive ? 'Yes' : 'No') },
    {
      field: 'preview', header: 'Preview Pop-up', align: 'center', width: 150, sortable: false, filterable: false, noExport: true,
      render: (r) => (
        <button type="button" className="ca-btn ps-preview" title={`Preview the “${r.name}” pop-up`} aria-label={`Preview ${r.name}`}
          onClick={(e) => { e.stopPropagation(); showPreview(r.name, { position: r.position, mode: r.mode, seconds: r.seconds }); }}>
          <i className="ti ti-bell-ringing" /> Preview
        </button>
      ),
    },
  ];
  const exp = gridExportMatrix(rows, columns);

  function onSelection(ids: Set<string | number>) {
    // One setting at a time — Edit / Delete act on the single selected row.
    const next = [...ids].filter((id) => !selected.has(id));
    setSelected(new Set(next.length ? [next[next.length - 1]!] : []));
  }

  return (
    <div className="nc-root ps-root">
      <div className="nc-topbar">
        <div className="nc-title-wrap">
          <FormTitle pageId={PAGE_ID} auditFormName={AUDIT_FORM} loadMs={loadMs} loading={loading} />
          <div className="nc-sub">Named pop-up settings for in-app (bell) alerts. Assign one to a rule, or let it follow the Default.</div>
        </div>
        <div className="nc-actions">
          <AppToolbar
            onNew={() => openDialog('new')}
            onEdit={() => selectedRow && openDialog('edit', selectedRow)}
            editDisabled={!selectedRow}
            onDelete={() => selectedRow && void requestDelete(selectedRow)}
            deleteDisabled={!selectedRow}
            onRefresh={() => void load()}
            refreshing={loading}
            exportData={{ filename: 'Pop-up Settings', ...exp }}
          />
        </div>
      </div>

      <div className="nc-scroll">
        <AppDataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={onSelection}
          onRowDoubleClick={(r) => openDialog('view', r)}
          emptyText="No pop-up settings yet. Click New to add one."
          exportFilename="Pop-up Settings"
          hideHeaderTools
        />
      </div>

      <AppDialog
        open={!!dlg}
        width={640}
        icon="ti-bell-cog"
        title={isNew ? 'New Pop-up Setting' : `${readOnly ? 'View' : 'Edit'} Pop-up Setting${dlg?.setting ? ` — ${dlg.setting.name}` : ''}`}
        onClose={closeDialog}
        submitOnEnter={!readOnly}
        footer={readOnly ? (
          <>
            <button type="button" className="ca-btn" onClick={closeDialog}>Close</button>
            <button type="button" className="ca-btn ca-btn-solid" onClick={() => dlg?.setting && openDialog('edit', dlg.setting)}>
              <i className="ti ti-pencil" /> Edit
            </button>
          </>
        ) : (
          <>
            <button type="button" className="ca-btn ps-test" onClick={showTest} title="Show a sample pop-up with the settings above">
              <i className="ti ti-bell-ringing" /> Show test pop-up
            </button>
            <button type="button" className="ca-btn" onClick={closeDialog}>Cancel</button>
            <button type="button" className="ca-btn ca-btn-solid" onClick={handleSave}>Save</button>
          </>
        )}
      >
        <div className="ps-form">
          <label className="ps-field">
            <span className="ps-flabel">Pop-up Name <span className="ps-req">*</span></span>
            <input type="text" className={`ps-name${!nameTrim && !readOnly ? ' ps-empty' : ''}`} maxLength={NAME_MAX} value={draft.name}
              disabled={readOnly} aria-required="true" aria-label="Pop-up Name"
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </label>

          <div className="ps-section">Position on screen</div>
          <div className="ps-grid" role="radiogroup" aria-label="Pop-up position">
            {ALERT_POPUP_POSITIONS.map((p) => (
              <button key={p} type="button" role="radio" aria-checked={draft.position === p} disabled={readOnly}
                className={`ps-cell${draft.position === p ? ' on' : ''}`} title={positionLabel(p)}
                onClick={() => setDraft((d) => ({ ...d, position: p }))}>
                <span className="ps-dot" />
                <span className="ps-cell-lbl">{positionLabel(p)}</span>
              </button>
            ))}
          </div>

          <div className="ps-section">How the pop-up closes</div>
          <div className="ps-modes">
            <label className="ps-mode">
              <input type="radio" name="ps-mode" checked={draft.mode === 'auto'} disabled={readOnly}
                onChange={() => setDraft((d) => ({ ...d, mode: 'auto' }))} />
              <span>Close automatically after</span>
              <input type="number" className="ps-seconds" min={ALERT_POPUP_MIN_SECONDS} max={ALERT_POPUP_MAX_SECONDS} step={1}
                value={draft.secondsText} disabled={readOnly || draft.mode !== 'auto'} aria-label="Seconds before the pop-up closes"
                aria-invalid={draft.mode === 'auto' && !secondsValid}
                onChange={(e) => setDraft((d) => ({ ...d, secondsText: e.target.value }))} />
              <span>seconds</span>
              {draft.mode === 'auto' && !secondsValid && !readOnly && (
                <span className="ps-err">Enter {ALERT_POPUP_MIN_SECONDS}–{ALERT_POPUP_MAX_SECONDS}.</span>
              )}
            </label>
            <label className="ps-mode">
              <input type="radio" name="ps-mode" checked={draft.mode === 'ack'} disabled={readOnly}
                onChange={() => setDraft((d) => ({ ...d, mode: 'ack' }))} />
              <span>Stay until the user clicks <b>Acknowledge</b> (who and when is recorded)</span>
            </label>
            <div className="ps-note"><i className="ti ti-info-circle" /> A rule marked ACK REQUIRED always needs Acknowledge, whatever is chosen here.</div>
          </div>

          <div className="ps-checks">
            <label className="ps-mode" title={lockedDefault ? 'Mark another setting as Default to change this one.' : undefined}>
              <input type="checkbox" checked={draft.isDefault} disabled={readOnly || lockedDefault}
                onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked, isActive: e.target.checked ? true : d.isActive }))} />
              <span>Default setting</span>
              <span className="ps-hint">— used by every rule that has no setting of its own</span>
            </label>
            <label className="ps-mode">
              <input type="checkbox" checked={draft.isActive || draft.isDefault} disabled={readOnly || draft.isDefault}
                onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))} />
              <span>Active</span>
              <span className="ps-hint">— an inactive setting is skipped and its rules follow the Default</span>
            </label>
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={!!replaceFor}
        width={460}
        icon="ti-star"
        title="Choose the new Default"
        onClose={() => setReplaceFor(null)}
        footer={(
          <>
            <button type="button" className="ca-btn" onClick={() => setReplaceFor(null)}>Cancel</button>
            <button type="button" className="ca-btn ca-btn-solid" onClick={() => void confirmReplacement()} disabled={!replacementId}>Continue</button>
          </>
        )}
      >
        <div className="ps-form">
          <p className="ps-note">
            “{replaceFor?.name}” is the Default setting. Pick which Active setting takes over before it is deleted.
          </p>
          <label className="ps-field">
            <span className="ps-flabel">New Default</span>
            <select className="ps-name" value={replacementId} onChange={(e) => setReplacementId(e.target.value)} aria-label="New Default setting">
              {rows.filter((r) => r.id !== replaceFor?.id && r.isActive).map((r) => (
                <option key={r.id} value={r.id}>{r.name} — {positionLabel(r.position)}, {closeLabel(r).toLowerCase()}</option>
              ))}
            </select>
          </label>
        </div>
      </AppDialog>
    </div>
  );
}
